/**
 * encodeViaWorker — high-level helper that combines the per-doc update log
 * with the client-side Yjs Web Worker to deliver "encode this doc's full
 * state" without a synchronous main-thread encode call.
 *
 * Mirrors `websocket-server/src/yjs/encodeViaPool.js`.
 *
 * Why this layer exists
 * ─────────────────────
 * The 80% browser use case is "I have a doc with an attached log, give me
 * its encoded state via the worker." Wraps that pattern so call sites
 * (notably the IDB stale-check that historically froze the UI) stay short
 * and consistent.
 *
 * Failure modes
 * ─────────────
 *   - If the worker is disabled (kill-switch via VITE_YJS_WEB_WORKER_ENABLED
 *     or localStorage), the client falls back to synchronous main-thread
 *     dispatch. We pay the encode cost on main thread, but the API is
 *     unchanged so callers don't branch.
 *   - If the worker throws (crash, timeout), this helper re-throws.
 *     `encodeDocViaWorkerBestEffort` swallows and returns null instead.
 *
 * Reference: `/workspace/.cursor/plans/websocket_robustness_refactor_b886ced2.plan.md`
 */

import type { DocUpdateLog } from './docUpdateLog';
import { getYjsWorkerClient } from './yjsWorkerClient';

interface YjsWorkerClientLike {
	encodeFromUpdates(updates: Uint8Array[]): Promise<Uint8Array>;
}

/**
 * Encode a doc's full state via the Web Worker, using its attached log.
 * On success, the log is compacted (replaced with the consolidated state)
 * unless `compact: false` is passed.
 */
export async function encodeDocViaWorker(
	log: DocUpdateLog,
	options: { compact?: boolean; client?: YjsWorkerClientLike } = {},
): Promise<Uint8Array> {
	const { compact = true, client = getYjsWorkerClient() } = options;

	if (!log || typeof log.snapshot !== 'function') {
		throw new TypeError('encodeDocViaWorker: log must be a docUpdateLog');
	}

	// Capture both the updates AND the generation marker so concurrent
	// updates that arrive on the doc's listener while the worker is
	// running aren't lost when we collapse the log. See
	// `docUpdateLog.replace(state, generation)` for the full contract.
	const { updates, generation } = log.snapshot();
	const encoded = await client.encodeFromUpdates(updates);

	if (compact && !log.isDestroyed()) {
		log.replace(encoded, generation);
	}

	return encoded;
}

/**
 * Best-effort variant: returns null on worker failure instead of throwing.
 * Use for fire-and-forget call sites where a worker hiccup shouldn't break
 * the surrounding code (e.g. the IDB stale-check; if it fails, we just
 * skip the optimization and continue).
 */
export async function encodeDocViaWorkerBestEffort(
	log: DocUpdateLog,
	options: {
		compact?: boolean;
		client?: YjsWorkerClientLike;
		logger?: { warn?: (...args: unknown[]) => void };
		logTag?: string;
	} = {},
): Promise<Uint8Array | null> {
	const { logger = console, logTag = '[encodeViaWorker]' } = options;
	try {
		return await encodeDocViaWorker(log, options);
	} catch (err) {
		logger.warn?.(`${logTag} encode failed: ${(err as Error).message}`);
		return null;
	}
}
